import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Typography, TableBody, TablePagination, TableFooter } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Container from '@material-ui/core/Container';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Grid from '@material-ui/core/Grid';
import { LoadingCircle } from '../LoadingCircle';

const useStyles = makeStyles(theme => ({
  wrapper: {
    paddingTop: theme.spacing(3),
  },
  grid: {
    paddingTop: theme.spacing(0),
    paddingBottom: theme.spacing(2.5),
  },
  sectionTitle: {
    borderBottom: '2px solid #DFDFDF',
    padding: theme.spacing(3),
  },
  button: {
    margin: theme.spacing(2.5),
  },
}));

const formatDate = ({ date, ...rest }) => ({
  ...rest,
  date: date ? new Date(date).toUTCString() : '',
});

const headCells = [
  { id: 'date', label: 'Date' },
  { id: 'event', label: 'Event' },
  { id: 'app', label: 'Application' },
  { id: 'ip', label: 'IP' },
  { id: 'location', label: 'Location' },
];

export const Log = PropTypes.shape({
  id: PropTypes.string,
  date: PropTypes.string,
  type: PropTypes.string,
  description: PropTypes.string,
  connection: PropTypes.string,
  ip: PropTypes.string,
  userAgent: PropTypes.string,
  grantType: PropTypes.string,
  strategy: PropTypes.string,
  isMobile: PropTypes.bool,
  strategyType: PropTypes.string,
  appName: PropTypes.string,
});

export const LogElement = ({ log }) => (
  <TableRow>
    {headCells.map(({ id }) => (
      <TableCell key={`log-${log.id}-${id}`}>{log[id]}</TableCell>
    ))}
  </TableRow>
);

LogElement.propTypes = {
  log: Log.isRequired,
};

export function PaginatedLogsTable({ paginatedLogs, update, userId, isLoading }) {
  const classes = useStyles();
  const { logs: currentLogs, start, page } = paginatedLogs;
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [userLogParams, setUserLogParams] = useState({ amount: rowsPerPage, page: 0, start: 0 });
  const [prevLogs, setPrevLogs] = useState([]);
  const logs = currentLogs && currentLogs.length ? currentLogs : prevLogs;
  const numberOfElements = start || userLogParams.start;
  const currentPage = page || userLogParams.page;
  const handleUpdate = params => {
    update({ id: userId, amount: rowsPerPage, page, ...params });
  };
  const handleChangePage = (_, newPage) => {
    const params = { page: newPage, amount: rowsPerPage, start };
    setUserLogParams({ page, ...params });
    setPrevLogs(logs);
    handleUpdate(params);
  };
  const handleChangeRowsPerPage = ({ target }) => {
    const amount = target.value;
    const params = { start, amount: target.value, page };
    setUserLogParams(params);
    setRowsPerPage(amount);
    setPrevLogs(logs);
    handleUpdate(params);
  };

  useEffect(() => {
    update({ id: userId, amount: rowsPerPage, page, ...userLogParams });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [update, userLogParams, userId, rowsPerPage]);

  return (
    <>
      <Grid className={classes.grid} maxwidth="lg">
        <Paper>
          <Grid container spacing={0} direction="row" wrap="nowrap" justify="space-between">
            <Typography className={classes.sectionTitle} variant="h5" component="h3">
              Logs
            </Typography>
          </Grid>
          <Container className={classes.wrapper} maxwidth="lg">
            {isLoading && !logs.length ? (
              <LoadingCircle isLoading={isLoading} />
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    {headCells.map(headCell => (
                      <TableCell key={headCell.id}>{headCell.label}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map(formatDate).map(log => (
                    <LogElement log={log} key={`log-${log.id}`} />
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TablePagination
                      count={numberOfElements}
                      rowsPerPage={rowsPerPage}
                      page={currentPage}
                      onChangePage={handleChangePage}
                      onChangeRowsPerPage={handleChangeRowsPerPage}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            )}
          </Container>
        </Paper>
      </Grid>
    </>
  );
}

const paginatedLogs = {
  logs: [],
  start: 0,
  pages: 0,
  page: 0,
};

PaginatedLogsTable.defaultProps = {
  isLoading: true,
  userId: null,
  paginatedLogs,
  update: () => paginatedLogs,
};

PaginatedLogsTable.propTypes = {
  isLoading: PropTypes.bool,
  userId: PropTypes.string,
  paginatedLogs: PropTypes.shape({
    logs: PropTypes.arrayOf(Log),
    page: PropTypes.number,
    pages: PropTypes.number,
    start: PropTypes.number,
  }),
  update: PropTypes.func,
};

export default PaginatedLogsTable;
